import React, { useState } from 'react';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { personalizePrompt, calculatePromptCompleteness, getIncompleteVariables, personalizePromptWithTemp, VARIABLE_SAMPLES } from '../../utils/promptPersonalization';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import { ClipboardDocumentIcon, UserIcon, EyeIcon, EyeSlashIcon, PencilIcon, SparklesIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface PersonalizedPromptViewProps {
  prompt: {
    id: number;
    title: string;
    content: string;
    chapterTitle: string;
    tags: string[];
  };
}

const PersonalizedPromptView: React.FC<PersonalizedPromptViewProps> = ({ prompt }) => {
  const { profile, isProfileComplete } = useUserProfile();
  const [showOriginal, setShowOriginal] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [showTempEditor, setShowTempEditor] = useState(false);
  const [tempValues, setTempValues] = useState<Record<string, string>>({});

  const personalizedContent = personalizePrompt(prompt.content, profile);
  const completeness = calculatePromptCompleteness(prompt.content, profile);
  const incompleteVariables = getIncompleteVariables(prompt.content, profile);
  
  // 임시 값이 있으면 임시 값으로 치환한 내용, 없으면 기본 개인화 내용
  const finalContent = Object.keys(tempValues).length > 0 
    ? personalizePromptWithTemp(personalizedContent, tempValues)
    : personalizedContent;

  const copyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('클립보드에 복사되었습니다!');
    } catch (err) {
      toast.error('복사에 실패했습니다.');
    }
  };

  const getCompletenessColor = (percentage: number) => {
    if (percentage >= 90) return 'text-green-600 bg-green-100';
    if (percentage >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getCompletenessText = (percentage: number) => {
    if (percentage >= 90) return '거의 완성';
    if (percentage >= 70) return '부분 완성';
    return '추가 정보 필요';
  };

  const handleSampleSelect = (variable: string, sample: string) => {
    setTempValues(prev => ({ ...prev, [variable]: sample }));
  };

  const handleDirectInput = (variable: string, value: string) => {
    setTempValues(prev => ({ ...prev, [variable]: value }));
  };

  const resetTempValues = () => {
    setTempValues({});
  };

  const generateRandomSamples = () => {
    const randomValues: Record<string, string> = {};
    incompleteVariables.forEach(variable => {
      const samples = VARIABLE_SAMPLES[variable];
      if (samples && samples.length > 0) {
        const randomIndex = Math.floor(Math.random() * samples.length);
        randomValues[variable] = samples[randomIndex];
      }
    });
    setTempValues(randomValues);
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">
            {prompt.title}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {prompt.chapterTitle}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCompletenessColor(completeness)}`}>
            {completeness}% {getCompletenessText(completeness)}
          </span>
        </div>
      </div>

      {/* 개인화 상태 */}
      {!isProfileComplete() && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-800">
            <UserIcon className="w-5 h-5" />
            <span className="font-medium">프로필을 완성하면 더 정확한 개인화가 가능합니다</span>
          </div>
          <p className="text-sm text-blue-600 mt-1">
            이름, 회사, 직책, 업종, 사업유형, 타겟시장 정보를 입력해주세요.
          </p>
        </div>
      )}

      {/* 미완성 변수 처리 */}
      {incompleteVariables.length > 0 && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <PencilIcon className="w-5 h-5 text-blue-500" />
                  추가 정보가 필요한 항목 ({incompleteVariables.length}개)
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  샘플을 선택하거나 직접 입력해서 완성된 프롬프트를 만들어보세요.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={generateRandomSamples}
                >
                  <SparklesIcon className="w-4 h-4 mr-1" />
                  랜덤 샘플
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowTempEditor(!showTempEditor)}
                >
                  {showTempEditor ? '닫기' : '편집'}
                </Button>
              </div>
            </div>

            {showTempEditor && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                {incompleteVariables.map((variable, index) => (
                  <div key={index} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {variable}
                    </label>
                    
                    {/* 샘플 선택 버튼들 */}
                    {VARIABLE_SAMPLES[variable] && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {VARIABLE_SAMPLES[variable].map((sample, sampleIndex) => (
                          <button
                            key={sampleIndex}
                            onClick={() => handleSampleSelect(variable, sample)}
                            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                              tempValues[variable] === sample
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                            }`}
                          >
                            {sample}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* 직접 입력 */}
                    <Input
                      placeholder="직접 입력하기"
                      value={tempValues[variable] || ''}
                      onChange={(value) => handleDirectInput(variable, value)}
                      fullWidth
                    />
                  </div>
                ))}
                
                <div className="flex justify-end gap-2 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={resetTempValues}
                  >
                    초기화
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(finalContent)}
                  >
                    <ClipboardDocumentIcon className="w-4 h-4 mr-1" />
                    완성본 복사
                  </Button>
                </div>
              </div>
            )}
            
            {/* 현재 임시 값들 표시 */}
            {Object.keys(tempValues).length > 0 && !showTempEditor && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(tempValues).map(([variable, value]) => (
                  <span
                    key={variable}
                    className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                  >
                    {variable}: {value}
                  </span>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* 프롬프트 내용 */}
      <Card>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">
              {showOriginal ? '원본 프롬프트' : '개인화된 프롬프트'}
            </h4>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowOriginal(!showOriginal)}
              >
                {showOriginal ? '개인화 보기' : '원본 보기'}
              </Button>
              <Button
                size="sm"
                onClick={() => copyToClipboard(showOriginal ? prompt.content : finalContent)}
              >
                <ClipboardDocumentIcon className="w-4 h-4 mr-1" />
                복사
              </Button>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <pre className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
              {showOriginal ? prompt.content : finalContent}
            </pre>
          </div>

          {/* 태그 */}
          {prompt.tags && prompt.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {prompt.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* 개인화 팁 */}
      {completeness < 100 && (
        <Card>
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">💡 개인화 팁</h4>
            <div className="text-sm text-gray-600 space-y-2">
              <p>• 프로필 정보를 더 자세히 입력하면 자동으로 변수가 채워집니다</p>
              <p>• 보유 스킬, 관심 분야, 사업 목표를 추가하면 더 정확한 프롬프트가 생성됩니다</p>
              <p>• 경험/배경 정보를 입력하면 상황에 맞는 표현으로 자동 변환됩니다</p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default PersonalizedPromptView;